import { useCallback, useEffect, useMemo, useState } from "react";

const CACHE_KEY = "sb_lang_dict_cache_v1";

function readCache() {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
  } catch {
    return null;
  }
}

function writeCache(cacheObj) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheObj));
  } catch {}
}

export function usePublicLanguage({ apiBase, defaultLang = "ar" } = {}) {
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || defaultLang);

  // ✅ boot from cache instantly
  const [dict, setDict] = useState(() => {
    const cache = readCache();
    return cache?.[lang] || null;
  });

  const [ready, setReady] = useState(() => {
    const cache = readCache();
    return !!cache?.[lang];
  });

  const isRTL = useMemo(() => lang === "ar" || lang === "he", [lang]);

  const t = useCallback(
    (key, fallback = "") => {
      // ✅ no flash: while loading return empty string (not fallback)
      if (!dict) return "";
      const parts = key.split(".");
      let cur = dict;
      for (const p of parts) {
        cur = cur?.[p];
        if (cur == null) return fallback;
      }
      return typeof cur === "string" ? cur : fallback;
    },
    [dict]
  );

  useEffect(() => {
    const controller = new AbortController();

    async function loadLang() {
      const cache = readCache();

      // ✅ use cache instantly
      if (cache?.[lang]) {
        setDict(cache[lang]);
        setReady(true);
      } else {
        setReady(false);
      }

      try {
        const res = await fetch(`${apiBase}/api/language/${lang}`, { signal: controller.signal });

        // if language missing → fallback to English
        if (!res.ok) {
          if (lang !== "en") setLang("en");
          return;
        }

        const data = await res.json();
        setDict(data || {});
        setReady(true);

        const next = { ...(cache || {}) };
        next[lang] = data || {};
        writeCache(next);
      } catch (e) {
        if (e.name !== "AbortError") console.error("Failed to load language:", e);
        setReady(true);
      }
    }

    loadLang();
    return () => controller.abort();
  }, [apiBase, lang]);

  // ✅ persist + set direction
  useEffect(() => {
    localStorage.setItem("lang", lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
  }, [lang, isRTL]);

  return { lang, setLang, dict, t, isRTL, ready };
}